/**
 * In-memory (session-only) record of when each attachment's CONTENT was last
 * served, feeding the eviction order in storageManager.
 *
 * Deliberately NOT persisted on read: stamping recency into the IndexedDB row
 * rewrites the WHOLE row (IDB has no partial update), and rewriting a row
 * INVALIDATES the file-backed Blob handles previously read from it — on
 * WebKit that killed the very thumbnail/image/video being displayed (broken
 * "?" images in bubbles, dead-URL videos surfacing the "can't be played"
 * card). READS MUST NEVER WRITE.
 *
 * Session-only is sufficient for the failure mode this exists for (watch →
 * swipe through others → swipe back, all within one session). Across
 * sessions, the persisted `lastAccessedAt` — stamped only on content WRITES,
 * where the row is being rewritten anyway — plus the message-timestamp
 * fallback take over.
 *
 * This module must stay dependency-free: it is imported from both the
 * IndexedDB service layer and the storage manager.
 */

const accessTimes = new Map<string, number>();

/** Record that this attachment's content was just served. Memory only. */
export function touchAttachmentAccess(attachmentId: string): void {
  accessTimes.set(attachmentId, Date.now());
}

/** Session access time, if any. */
export function getAttachmentAccessTime(
  attachmentId: string,
): number | undefined {
  return accessTimes.get(attachmentId);
}

/** Test-only reset. */
export function __resetAttachmentAccessTimes(): void {
  accessTimes.clear();
}
